import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiClient from '../api/client';

export interface BrandingConfig {
  portalName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

interface BrandingContextType {
  config: BrandingConfig;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const defaultBranding: BrandingConfig = {
  portalName: 'Xtrem Admin Portal',
  logoUrl: '/logo.png',
  faviconUrl: '/favicon.ico',
  primaryColor: '#1E3A8A',
  secondaryColor: '#0056B3',
  accentColor: '#009688'
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const getStoredBranding = (): BrandingConfig => {
  try {
    const cached = localStorage.getItem('admin_branding_config');
    if (cached) {
      const parsed = JSON.parse(cached);
      return { ...defaultBranding, ...parsed };
    }
  } catch (e) {
    console.error('Failed to parse cached branding:', e);
  }
  return defaultBranding;
};

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<BrandingConfig>(getStoredBranding);
  const [loading, setLoading] = useState(true);

  const applyBranding = (cfg: BrandingConfig) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', cfg.primaryColor);
    root.style.setProperty('--brand-secondary', cfg.secondaryColor);
    root.style.setProperty('--brand-accent', cfg.accentColor);
    document.title = cfg.portalName;
    const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = cfg.faviconUrl;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = cfg.faviconUrl;
      document.head.appendChild(newLink);
    }
  };

  // Synchronously apply branding colors before browser paint
  React.useLayoutEffect(() => {
    applyBranding(config);
  }, [config]);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await apiClient.get<Partial<BrandingConfig>>('/admin/companies/branding', {
        skipAuth: true
      } as any);
      if (res.data) {
        const mergedConfig = {
          ...defaultBranding,
          ...res.data
        };
        setConfig(mergedConfig);
        try {
          localStorage.setItem('admin_branding_config', JSON.stringify(mergedConfig));
        } catch (_) {}
      }
    } catch (err) {
      console.warn('Failed to fetch branding from API, using defaults:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Listen for branding updates broadcast by the Config page after save
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('branding_update');
      bc.onmessage = (event) => {
        if (event.data?.type === 'BRANDING_UPDATED' && event.data?.branding) {
          const updated: BrandingConfig = { ...defaultBranding, ...event.data.branding };
          setConfig(updated);
          try {
            localStorage.setItem('admin_branding_config', JSON.stringify(updated));
          } catch (_) {}
        }
      };
    } catch (_) {}
    return () => { bc?.close(); };
  }, []);

  return (
    <BrandingContext.Provider value={{ config, loading, refreshBranding: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
